import {keepPreviousData, useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {AgentsSidebarProps} from '@github-ui/agent-sessions/components/agents-view/AgentsSidebar'

type ResolvedRepositoryAgentEnabledById = Record<string, boolean>
type RepositoryAgentEnabledById = Record<string, boolean | null>
export type RepositoryTaskGroups = NonNullable<AgentsSidebarProps['groups']>

const EMPTY_ENABLEMENT: RepositoryAgentEnabledById = {}

interface RepositoryAgentEnablementResponse {
  repositoryAgentEnabledById: ResolvedRepositoryAgentEnabledById
}

export function repositoryIdsFromGroups(groups: RepositoryTaskGroups): number[] {
  const repositoryIds = groups.flatMap(([, tasks]) =>
    tasks.flatMap(task => (task.repo_id !== undefined && task.repo_id > 0 ? [task.repo_id] : [])),
  )
  return [...new Set(repositoryIds)].sort((a, b) => a - b)
}

export function useRepositoryAgentEnablement(groups: RepositoryTaskGroups, enabled = true) {
  const repositoryIds = repositoryIdsFromGroups(groups)

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Repository policy is viewer-scoped and must not be persisted; this Rails endpoint has no generated response schema.
  const query = useQuery({
    queryKey: ['repository-agent-enablement', repositoryIds],
    queryFn: async (): Promise<RepositoryAgentEnabledById> => {
      const params = new URLSearchParams({repository_ids: repositoryIds.join(',')})
      const response = await reactFetchJSON(`/copilot/agent-sessions/repository-agent-enablement?${params.toString()}`)
      if (!response.ok) {
        throw new Error(`Failed to load repository agent enablement: ${response.status}`)
      }

      const data = (await response.json()) as RepositoryAgentEnablementResponse
      return Object.fromEntries(
        repositoryIds.map(repositoryId => {
          const key = repositoryId.toString()
          return [key, data.repositoryAgentEnabledById[key] ?? true]
        }),
      )
    },
    enabled: enabled && repositoryIds.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  if (!enabled) return EMPTY_ENABLEMENT
  if (query.isError) return EMPTY_ENABLEMENT

  const resolvedEnablement = query.data ?? EMPTY_ENABLEMENT
  if (!query.isFetching) return resolvedEnablement

  return Object.fromEntries(
    repositoryIds.map(repositoryId => {
      const key = repositoryId.toString()
      return [key, resolvedEnablement[key] ?? null]
    }),
  )
}
